import React, { useEffect, useState } from 'react';
import { Table, Button, Space, message, Card, Tag, Select, Input } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchSectionCodes } from '../../api';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

const { Option } = Select;

const typeColorMap: Record<string, string> = {
    tourInfo: 'green',
    contactList: 'orange',
    itinerary: 'purple',
    accommodation: 'cyan',
};

const typeNameMap: Record<string, string> = {
    tourInfo: '旅行團資料',
    contactList: '聯絡人名單',
    itinerary: '行程',
    accommodation: '住宿',
};

export default function SectionCodesList() {
    const [data, setData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>('');

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        filterData();
    }, [data, typeFilter, searchText]);

    const load = async () => {
        setLoading(true);
        try {
            const resp = await fetchSectionCodes();
            const result = await resp.json();
            if (result.success) {
                setData(result.data || []);
            }
        } catch (err) {
            console.error(err);
            message.error('載入失敗');
        } finally { 
            setLoading(false); 
        }
    };

    const filterData = () => {
        let filtered = [...data];
        
        if (typeFilter) {
            filtered = filtered.filter(item => item.type === typeFilter);
        }
        
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(item => 
                item.code?.toLowerCase().includes(search) || 
                item.name?.toLowerCase().includes(search)
            );
        }
        
        setFilteredData(filtered);
    };

    const columns = [
        { 
            title: '代碼', 
            dataIndex: 'code', 
            render: (val: any) => <b style={{ color: '#1890ff' }}>{val}</b>,
            width: 150,
        },
        { 
            title: '類型', 
            dataIndex: 'type',
            render: (val: string) => (
                <Tag color={typeColorMap[val] || 'default'}>
                    {typeNameMap[val] || val}
                </Tag>
            ),
            width: 120,
        },
        { 
            title: '名稱', 
            dataIndex: 'name',
            ellipsis: true,
        },
        { 
            title: '擁有者', 
            dataIndex: 'owner',
            width: 150,
        },
        { 
            title: '更新時間', 
            dataIndex: 'updated_at',
            render: (val: any) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-',
            width: 180,
        },
        { 
            title: '操作', 
            dataIndex: 'code', 
            render: (val: any) => (
                <Link to={`/section-codes/${val}`}>
                    <Button type="link" size="small">查看</Button>
                </Link>
            ),
            width: 100,
        }
    ];

    return (
        <div style={{ 
            padding: '24px',
            backgroundColor: '#f5f7fa',
            minHeight: 'calc(100vh - 80px)'
        }}>
            <Card 
                title={<span style={{ fontSize: '18px', fontWeight: 600 }}>區段代碼管理</span>}
                bordered={false}
                style={{ 
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)'
                }}
            >
                <Space 
                    style={{ 
                        marginBottom: 16, 
                        width: '100%', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap'
                    }}
                >
                    <Space wrap>
                        <Input
                            placeholder="搜尋代碼或名稱"
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                        />
                        <Select
                            placeholder="選擇類型"
                            style={{ width: 150 }}
                            value={typeFilter}
                            onChange={setTypeFilter}
                            allowClear
                        >
                            <Option value="tourInfo">旅行團資料</Option>
                            <Option value="contactList">聯絡人名單</Option>
                            <Option value="itinerary">行程</Option>
                            <Option value="accommodation">住宿</Option>
                        </Select>
                        <Button 
                            icon={<ReloadOutlined />} 
                            onClick={load}
                        >
                            重新整理
                        </Button>
                    </Space>
                    <Link to="/section-codes/new">
                        <Button type="primary" icon={<PlusOutlined />}>
                            新增代碼
                        </Button>
                    </Link>
                </Space>
                <Table 
                    rowKey={(r: any) => r.code} 
                    loading={loading} 
                    dataSource={filteredData} 
                    columns={columns}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 筆記錄`,
                        defaultPageSize: 20,
                    }}
                    style={{ backgroundColor: 'white' }}
                />
            </Card>
        </div>
    );
}
